/**
 * Serviço de deduplicação de requisições
 * Evita requests duplicadas durante navegação rápida
 * Reutiliza a mesma Promise para requisições idênticas
 */

class RequestDeduplicationService {
  constructor() {
    // Cache de requisições em voo
    this.pendingRequests = new Map();
    
    // Configurações
    this.defaultTimeout = 30000; // 30 segundos
    this.cleanupInterval = 60000; // 1 minuto
    
    // Iniciar limpeza automática
    this.startCleanup();
  }

  /**
   * Gera chave única para a requisição
   * @param {string} url - URL da requisição
   * @param {Object} params - Parâmetros da requisição
   * @param {string} method - Método HTTP
   * @returns {string} - Chave única
   */
  generateKey(url, params = {}, method = 'GET') {
    const sortedParams = Object.keys(params)
      .sort()
      .reduce((result, key) => {
        result[key] = params[key];
        return result;
      }, {});
    
    return `${method}:${url}:${JSON.stringify(sortedParams)}`;
  }

  /**
   * Executa requisição com deduplicação
   * @param {Function} requestFn - Função que executa a requisição
   * @param {string} url - URL da requisição
   * @param {Object} params - Parâmetros da requisição
   * @param {string} method - Método HTTP
   * @param {number} timeout - Timeout em ms
   * @returns {Promise} - Promise da requisição
   */
  async executeRequest(requestFn, url, params = {}, method = 'GET', timeout = this.defaultTimeout) {
    const key = this.generateKey(url, params, method);
    
    // Se já existe uma requisição em voo, reutilizar
    if (this.pendingRequests.has(key)) {
      return this.pendingRequests.get(key);
    }

    // Criar nova requisição
    const requestPromise = this.createRequest(requestFn, url, params, timeout);
    
    // Armazenar no cache
    this.pendingRequests.set(key, requestPromise);
    
    // Limpar do cache quando completar
    requestPromise
      .finally(() => {
        this.pendingRequests.delete(key);
      });

    return requestPromise;
  }

  /**
   * Cria requisição com timeout
   * @param {Function} requestFn - Função que executa a requisição
   * @param {string} url - URL da requisição
   * @param {Object} params - Parâmetros da requisição
   * @param {number} timeout - Timeout em ms
   * @returns {Promise} - Promise da requisição
   */
  async createRequest(requestFn, url, params, timeout) {
    return Promise.race([
      requestFn(url, params),
      new Promise((_, reject) => 
        setTimeout(() => reject(new Error('Request timeout')), timeout)
      )
    ]);
  }

  /**
   * Verifica se existe requisição em voo
   * @param {string} url - URL da requisição
   * @param {Object} params - Parâmetros da requisição
   * @param {string} method - Método HTTP
   * @returns {boolean} - True se existe requisição em voo
   */
  hasPendingRequest(url, params = {}, method = 'GET') {
    const key = this.generateKey(url, params, method);
    return this.pendingRequests.has(key);
  }

  /**
   * Cancela requisição em voo
   * @param {string} url - URL da requisição
   * @param {Object} params - Parâmetros da requisição
   * @param {string} method - Método HTTP
   * @returns {boolean} - True se cancelou com sucesso
   */
  cancelRequest(url, params = {}, method = 'GET') {
    const key = this.generateKey(url, params, method);
    const cancelled = this.pendingRequests.delete(key);
    return cancelled;
  }

  /**
   * Limpa todas as requisições em voo
   */
  clearAllRequests() {
    this.pendingRequests.clear();
  }

  /**
   * Obtém estatísticas do cache
   * @returns {Object} - Estatísticas do cache
   */
  getStats() {
    return {
      pendingRequests: this.pendingRequests.size,
      keys: Array.from(this.pendingRequests.keys())
    };
  }

  /**
   * Inicia limpeza automática de requisições antigas
   */
  startCleanup() {
    setInterval(() => {
      const now = Date.now();
      
      for (const [key, promise] of this.pendingRequests.entries()) {
        // Se a requisição tem mais de 5 minutos, limpar
        if (promise.timestamp && (now - promise.timestamp) > 300000) {
          this.pendingRequests.delete(key);
        }
      }
      
      // Limpeza automática concluída
    }, this.cleanupInterval);
  }

  /**
   * Destrói o serviço
   */
  destroy() {
    this.clearAllRequests();
    if (this.cleanupTimer) {
      clearInterval(this.cleanupTimer);
    }
  }
}

// Instância singleton
const requestDeduplicationService = new RequestDeduplicationService();

export default requestDeduplicationService;
